import { gql } from '@apollo/client'
import { uniq } from 'ramda'

import { serviceOrdersClient } from '../../apollo/api'
import { useAlertModal, useOpenBulkExecuteServiceOrdersModal } from '../../routes/modals/modalsActions'
import { useServiceOrderPermissions } from '../../permissions/serviceOrders/serviceOrderPermissions'

// Máximo de atividades permitidas em uma execução/edição em massa (mesmo
// limite validado no backend).
export const MAX_BULK_SERVICE_ORDERS = 30

// Mesmos nomes de status considerados "em edição" (não concluída) pela tela
// de execução individual (ver executionV2/_data/isEditing.js) — reaproveitado
// aqui para decidir se a seleção é "toda aberta" ou "toda concluída".
const OPEN_STATUS_NAMES = ['received', 'pause', 'execution', 'open', 'pending']

const ELIGIBILITY_QUERY = gql`
  query ServiceOrdersBulkExecutionEligibility($ids: [Int]) {
    serviceOrders(filter: { ids: $ids, ignoreSegments: true }, limit: 999) {
      id
      checklistId
      status {
        id
        name
      }
      checklist {
        id
        allowBulkExecution
      }
    }
  }
`

const checklistIdsOf = serviceOrders => uniq(serviceOrders.map(so => so.checklistId))
const groupOf = so => (OPEN_STATUS_NAMES.includes(so.status?.name) ? 'open' : 'done')
const groupsOf = serviceOrders => uniq(serviceOrders.map(groupOf))
// Ausente/`null` é tratado como permitido (compatibilidade com checklists
// antigos ou com o campo ainda não disponível no schema).
const hasChecklistThatDisallowsBulk = serviceOrders => serviceOrders.some(so => so.checklist?.allowBulkExecution === false)

// Faz uma checagem rápida (mesmo questionário; todas abertas OU todas
// concluídas; nenhum questionário bloqueado para execução em massa) antes de
// abrir a tela de execução em massa. O backend valida tudo de novo (não
// confiamos apenas no cliente), mas isso dá um retorno imediato e amigável ao
// usuário sem precisar abrir o formulário inteiro.
export const useCheckBulkExecutionEligibility = () => {
  return async ids => {
    const { data, errors } = await serviceOrdersClient.query({
      query: ELIGIBILITY_QUERY,
      variables: { ids },
      fetchPolicy: 'no-cache'
    })

    if (errors?.length) {
      return { ok: false, reason: 'error' }
    }

    const serviceOrders = data?.serviceOrders || []

    if (serviceOrders.length !== ids.length) return { ok: false, reason: 'notFound' }
    if (checklistIdsOf(serviceOrders).length > 1) return { ok: false, reason: 'differentChecklists' }
    if (groupsOf(serviceOrders).length > 1) return { ok: false, reason: 'mixedStatusGroups' }
    if (hasChecklistThatDisallowsBulk(serviceOrders)) return { ok: false, reason: 'bulkExecutionNotAllowed' }

    return { ok: true }
  }
}

export const useBulkExecuteServiceOrdersAction = ids => {
  const openBulkExecutionModal = useOpenBulkExecuteServiceOrdersModal()
  const openAlert = useAlertModal()
  const checkEligibility = useCheckBulkExecutionEligibility()
  const { canExecuteFromWeb, canEdit, canEditFinishedServiceOrders } = useServiceOrderPermissions()

  const hasPermission =
    ids?.length >= 2 &&
    ids?.length <= MAX_BULK_SERVICE_ORDERS &&
    (canExecuteFromWeb || (canEdit && canEditFinishedServiceOrders))

  const click = async () => {
    if (!ids?.length) return

    if (ids.length > MAX_BULK_SERVICE_ORDERS) {
      return openAlert(
        'Limite de atividades excedido',
        `Selecione no máximo ${MAX_BULK_SERVICE_ORDERS} atividades por vez para executar em massa.`,
        { icon: 'BsInfoCircle' }
      )
    }

    const eligibility = await checkEligibility(ids)

    if (eligibility.ok) return openBulkExecutionModal(ids)

    if (eligibility.reason === 'differentChecklists') {
      return openAlert(
        'Questionários diferentes',
        'Só é possível executar em massa atividades que usam o mesmo questionário. Selecione apenas atividades com o mesmo questionário e tente novamente.',
        { icon: 'BsInfoCircle' }
      )
    }

    if (eligibility.reason === 'mixedStatusGroups') {
      return openAlert(
        'Seleção com atividades abertas e concluídas',
        'Não é possível agir sobre atividades abertas e atividades já concluídas ao mesmo tempo. Selecione apenas atividades abertas ou apenas atividades concluídas.',
        { icon: 'BsInfoCircle' }
      )
    }

    if (eligibility.reason === 'bulkExecutionNotAllowed') {
      return openAlert(
        'Execução em massa não permitida',
        'Uma ou mais atividades selecionadas possuem questionários que não permitem execução em massa. Revise a seleção para continuar.',
        { icon: 'BsInfoCircle' }
      )
    }

    return openAlert(
      'Não foi possível continuar',
      'Não foi possível carregar as atividades selecionadas. Atualize a lista e tente novamente.',
      { icon: 'BsInfoCircle' }
    )
  }

  return { click, hasPermission }
}
